/**
 * Presents the org's destination-keyed `fieldMappings` source-first, which is
 * how both mapping previews list them: the product modal's tables and the
 * configuration sidebar's mapping table show what the response returned, one row
 * per source. Consumers: `useSignifyProductModal`,
 * `useSignifyConfigurationSidebar`.
 *
 * Resolution runs once per response, against the response
 * `indexSignifyProductPrices` produced, and only destinations that exist on the
 * Products entity survive. Inverting is lossy in one direction: two destinations
 * may legitimately read one source, and only the first keeps the row, because a
 * session override is keyed by source and so can name only one destination.
 * A constant source has no response row at all, so those destinations come back
 * separately, read-only — a session cannot override a constant, the accepted
 * limit of SYD-2424.
 */
import {
  resolveSignifyFieldValues,
  type SignifyFieldCondition,
  type SignifyFieldMappings,
  type SignifyValueSource,
} from '#types';

/** Why one source row holds its destination: the rule that matched, and its whole fallback list. */
export type SignifySourceRule = {
  ruleIndex: number;
  when?: SignifyFieldCondition;
  sources: SignifyValueSource[];
};

export type SignifyConstantMapping = {
  fieldId: string;
  value: string | number | boolean;
};

export type ResolvedSignifySourceMappings = {
  /** Response path -> the destination resolved from it. */
  sourceMappings: Record<string, string>;
  /** Response path -> the rule that supplied its destination. */
  sourceRules: Record<string, SignifySourceRule>;
  /** Destinations a constant filled, in configuration order. */
  constants: SignifyConstantMapping[];
};

export const resolveSignifySourceMappings = (params: {
  fieldMappings: SignifyFieldMappings;
  response: Record<string, unknown>;
  availableFieldIds: string[];
}): ResolvedSignifySourceMappings => {
  const { fieldMappings, response, availableFieldIds } = params;
  const available = new Set(availableFieldIds);
  const resolved = resolveSignifyFieldValues({ fieldMappings, response });

  return Object.entries(resolved).reduce<ResolvedSignifySourceMappings>(
    (accumulated, [fieldId, field]) => {
      if (!available.has(fieldId)) return accumulated;

      if (field.source.type === 'constant') {
        return {
          ...accumulated,
          constants: [...accumulated.constants, { fieldId, value: field.source.value }],
        };
      }

      const { path } = field.source;
      if (accumulated.sourceMappings[path] !== undefined) return accumulated;

      const rule = fieldMappings[fieldId][field.ruleIndex];
      return {
        ...accumulated,
        sourceMappings: { ...accumulated.sourceMappings, [path]: fieldId },
        sourceRules: {
          ...accumulated.sourceRules,
          [path]: { ruleIndex: field.ruleIndex, when: rule.when, sources: rule.sources },
        },
      };
    },
    { sourceMappings: {}, sourceRules: {}, constants: [] }
  );
};
